/**
 * Applicant-pool figures for the Maintenance Manager report.
 *
 * Transcribed from `Maintenance_Manager_Applicant_Summary.docx` (source:
 * SYT0070 applicant export). Kept in one module so the page renders off a
 * plain data shape - swap this for an API response with the same keys when the
 * endpoint lands, and nothing in the page or the chart components changes.
 *
 * Note: the source document's summary table lists 42 for "Pharma (API &
 * Interm.)" while its own chart and the pool total both say 45. 45 is used
 * here - it is the figure that reconciles to 174.
 */

export const TOTAL_APPLICANTS = 174;

const maintenanceReport = {
  meta: {
    eyebrow: "Recruitment Analytics",
    title: "Maintenance Manager — Applicant Pool Summary",
    subtitle: `Pharmaceutical Manufacturing · ${TOTAL_APPLICANTS} Applicants Screened`,
    source: "Prepared for hiring review · Source: SYT0070 applicant export",
  },

  headline: [
    { label: "Total applicants", value: TOTAL_APPLICANTS },
    { label: "Avg. years exp.", value: "12.4" },
    { label: "Top-fit (90+)", value: 42 },
    { label: "Bachelor's +", value: 128 },
  ],

  experience: [
    { name: "0–4 yrs", value: 15 },
    { name: "5–9 yrs", value: 35 },
    { name: "10–14 yrs", value: 69 },
    { name: "15–19 yrs", value: 36 },
    { name: "20+ yrs", value: 19 },
  ],

  buyOut: [
    { name: "Buy-out available", value: 100 },
    { name: "No buy-out", value: 74 },
  ],

  /* N/A last so the reserved neutral sits at the end of the ring. */
  education: [
    { name: "Bachelor's", value: 128 },
    { name: "Diploma", value: 27 },
    { name: "Master's", value: 12 },
    { name: "PG Diploma", value: 1 },
    { name: "N/A", value: 6 },
  ],

  discipline: [
    { name: "Mechanical Engg", value: 90 },
    { name: "Electrical Engg", value: 40 },
    { name: "Electronics Engg", value: 7 },
    { name: "Engineering (Gen)", value: 5 },
    { name: "Project Mgmt", value: 3 },
    { name: "Pharma Engg", value: 3 },
    { name: "Others", value: 26 },
  ],

  industry: [
    { name: "Pharma (API & Interm.)", value: 45 },
    { name: "Engg & Construction", value: 8 },
    { name: "FMCG", value: 8 },
    { name: "Cement", value: 7 },
    { name: "Chemicals", value: 7 },
    { name: "Pharma (Fin. Dosage)", value: 5 },
    { name: "Fertilizers/Agro", value: 5 },
    { name: "Iron & Steel", value: 5 },
    { name: "Other industries", value: 69 },
    { name: "N/A", value: 15 },
  ],

  /* Only the 82 applicants who sat the quiz. */
  quizScores: [
    { name: "1", value: 3 },
    { name: "2", value: 4 },
    { name: "3", value: 10 },
    { name: "4", value: 14 },
    { name: "5", value: 8 },
    { name: "6", value: 6 },
    { name: "7", value: 5 },
    { name: "8", value: 12 },
    { name: "9", value: 18 },
    { name: "10", value: 2 },
  ],

  /* Ordered bands - drawn on the ordinal ramp, best band darkest. */
  fitment: [
    { name: "Excellent (90–100)", value: 42 },
    { name: "Good (50–69)", value: 18 },
    { name: "Normal (<50)", value: 114 },
  ],

  screening: [
    { name: "Q1: Chemical Engineer", yes: 2, no: 166, na: 6 },
    { name: "Q2: Handled Maint. + Projects", yes: 165, no: 3, na: 6 },
    { name: "Q3: Reported to MD directly", yes: 97, no: 71, na: 6 },
    { name: "Q4: Process Engg knowledge", yes: 143, no: 25, na: 6 },
  ],

  narrative: {
    snapshot:
      "The role attracted 174 applicants, a strong and experienced pool. The median candidate carries 10–14 years of experience, and the field skews heavily toward core Mechanical and Electrical engineering backgrounds. Encouragingly, 42 candidates score in the top fitment band (90+), while 55 come directly from pharmaceutical or chemical-process industries — the closest-adjacent talent for this role.",
    experience:
      "Two-thirds of applicants (140 of 174) fall in the 5–19 year band — the sweet spot for a manager-level maintenance hire. 100 candidates indicate a notice buy-out is available, easing onboarding timelines.",
    education:
      "The pool is well-qualified: 128 hold a Bachelor's degree or higher, with a further 27 diploma-holders. Mechanical Engineering dominates the discipline mix, followed by Electrical.",
    industry:
      "Pharmaceutical API & intermediates is the single largest source (45 applicants). Combined with finished-dosage pharma, chemicals, fertilizers and cement, the pool is rich in regulated / process-manufacturing experience directly relevant to plant maintenance.",
    assessment:
      "Technical quiz results are bimodal — a strong cluster scoring 8–10 (32 candidates) alongside a mid-band. The overall fitment score concentrates 42 candidates in the Excellent band, which should anchor the shortlist.",
    screening:
      "Four custom questions targeted the role's must-haves. Standouts: 165 candidates have handled both maintenance and projects, and 143 bring process-engineering knowledge — both central to this position.",
  },

  keyDistributions: [
    { dimension: "Experience", segment: "10–14 years", count: 69 },
    { dimension: "Education", segment: "Bachelor's degree", count: 128 },
    { dimension: "Discipline", segment: "Mechanical Engineering", count: 90 },
    { dimension: "Industry", segment: "Pharma (API & Interm.)", count: 45 },
    { dimension: "Fitment", segment: "Excellent (90–100)", count: 42 },
    { dimension: "Quiz", segment: "Scored 8–10 of 10", count: 32 },
    { dimension: "Maint. + Projects", segment: 'Answered "Yes"', count: 165 },
    { dimension: "Process Engg.", segment: 'Answered "Yes"', count: 143 },
    { dimension: "Buy-out", segment: "Available", count: 100 },
  ],

  shortlistLens: [
    "Fitment ≥ 90 — focuses on the 42 top-band candidates.",
    "Pharma / chemical / process industry — ~55 candidates with directly transferable regulated-plant experience.",
    "10+ years experience — ensures manager-level depth (124 candidates qualify).",
    'Answered "Yes" to Maintenance + Projects and Process Engineering — the role\'s two functional must-haves.',
  ],
};

export default maintenanceReport;
